import { memo, useMemo, useState } from 'react'

import { Autocomplete, ListItemText, TextField } from '@mui/material'
import { AudioStream, VideoStream } from '@nativewaves/nw-cloud-sdk/content'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { debounce } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { ListBoxInfinite, fetchNextPageOnBottomReached } from '@shared/components/MaterialUIEnhancements/AutoComplete'
import { audioStreamQueryKeys, videoStreamQueryKeys } from 'services/queryKeys'

type Stream = VideoStream | AudioStream

export type StreamSelectorProps = {
  repositoryId?: string
  kind: 'video' | 'audio'
  streamId?: string
  disabled?: boolean
  onChange: (streamId: string) => void
}

const StreamSelectorRaw = ({ repositoryId, kind, streamId, disabled, onChange }: StreamSelectorProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [queryValue, setQueryValue] = useState<string>()

  const isVideo = kind === 'video'

  // Resolve the currently-selected stream so we can show its name (not just the id).
  const videoDetailQuery = useQuery({
    ...videoStreamQueryKeys.detail({ streamId: streamId! }),
    enabled: isVideo && !!streamId,
  })
  const audioDetailQuery = useQuery({
    ...audioStreamQueryKeys.detail({ streamId: streamId! }),
    enabled: !isVideo && !!streamId,
  })
  const detailData = (isVideo ? videoDetailQuery.data : audioDetailQuery.data) ?? null

  const videoListQuery = useInfiniteQuery({
    ...videoStreamQueryKeys.list({ repositoryId: repositoryId!, query: queryValue }),
    enabled: isVideo && !!repositoryId && !disabled,
  })
  const audioListQuery = useInfiniteQuery({
    ...audioStreamQueryKeys.list({ repositoryId: repositoryId!, query: queryValue }),
    enabled: !isVideo && !!repositoryId && !disabled,
  })
  const listData = isVideo ? videoListQuery.data : audioListQuery.data
  const items = (listData?.pages.flatMap((page) => page.items) ?? []) as Stream[]
  const isListLoading = isVideo ? videoListQuery.isLoading : audioListQuery.isLoading
  const hasNextPage = isVideo ? videoListQuery.hasNextPage : audioListQuery.hasNextPage
  const isFetchingNextPage = isVideo ? videoListQuery.isFetchingNextPage : audioListQuery.isFetchingNextPage
  const fetchNextPage = isVideo ? videoListQuery.fetchNextPage : audioListQuery.fetchNextPage
  // Prefer the just-picked option from the loaded list so the selection shows instantly;
  // the per-id detail query for a freshly selected stream isn't cached yet.
  const selected = (streamId != null ? items.find((item) => item.id === streamId) : undefined) ?? detailData
  // Keep the selected value present in the option list to avoid MUI's out-of-range warning.
  const options = selected && !items.some((item) => item.id === selected.id) ? [selected, ...items] : items

  const debouncedSetQueryValue = useMemo(
    () => debounce((value: string) => setQueryValue(value), 400, { leading: true }),
    [],
  )

  if (disabled) {
    return (
      <TextField
        size="small"
        fullWidth
        disabled
        label={t('domain:Workspace.Event.ConfigEditor.stream')}
        value={selected?.name || streamId || ''}
        helperText={t('domain:Workspace.Event.ConfigEditor.advancedStreamHint')}
      />
    )
  }

  return (
    <Autocomplete<Stream>
      size="small"
      fullWidth
      openOnFocus
      autoHighlight
      blurOnSelect
      options={options}
      loading={isListLoading}
      value={selected}
      onChange={(_, value) => value && onChange(value.id)}
      onInputChange={(_, value, reason) => {
        if (reason === 'input') {
          debouncedSetQueryValue(value)
        }
      }}
      filterOptions={(option) => option}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name || `${t('id')} (${option.id})`}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <ListItemText primary={option.name || `${t('id')} (${option.id})`} secondary={option.id} />
        </li>
      )}
      ListboxComponent={ListBoxInfinite}
      ListboxProps={{
        onScroll: fetchNextPageOnBottomReached({
          hasNextPage,
          isFetchingNextPage,
          fetchNextPage,
        }),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('domain:Workspace.Event.ConfigEditor.stream')}
          placeholder={t('domain:Workspace.Event.ConfigEditor.selectStream')}
        />
      )}
    />
  )
}

export const StreamSelector = memo(StreamSelectorRaw)
